import { useMemo, useState } from "react";
import { CaretDown, CaretUp, DownloadSimple } from "@phosphor-icons/react";
import { ChartCard } from "./StatsCharts";
import { downloadCsv, eur, num } from "./statsUtils";

const TABS = [
  { id: "properties", label: "Objekti" },
  { id: "room_types", label: "Tipovi" },
  { id: "units", label: "Jedinice" },
];
const COLS = [
  { key: "name", label: "Naziv", align: "left" },
  { key: "revenue", label: "Prihod", fmt: (v) => eur(v) },
  { key: "occupancy", label: "Zauzeće", fmt: (v) => `${num(v, 1)} %` },
  { key: "adr", label: "ADR", fmt: (v) => eur(v, 2) },
  { key: "revpar", label: "RevPAR", fmt: (v) => eur(v, 2) },
  { key: "nights", label: "Noćenja", fmt: (v) => num(v) },
  { key: "bookings", label: "Rez.", fmt: (v) => num(v) },
];

export function BreakdownTable({ breakdown, range }) {
  const [tab, setTab] = useState("properties");
  const [sort, setSort] = useState({ key: "revenue", dir: -1 });
  const rows = useMemo(() => {
    const r = [...(breakdown[tab] || [])];
    r.sort((a, b) => (a[sort.key] > b[sort.key] ? 1 : a[sort.key] < b[sort.key] ? -1 : 0) * sort.dir);
    return r;
  }, [breakdown, tab, sort]);
  const maxOcc = Math.max(1, ...rows.map((r) => r.occupancy));
  const toggle = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }));

  return (
    <ChartCard
      title="Usporedba"
      subtitle="Klikni na stupac za sortiranje"
      testId="stats-breakdown"
      action={
        <button
          type="button"
          onClick={() => downloadCsv(`statistika-${tab}-${range.from}_${range.to}.csv`, rows, COLS.map((c) => ({ ...c, csv: (r) => (c.key === "name" ? r.name : r[c.key]) })))}
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium hover:bg-muted"
          data-testid="stats-breakdown-csv"
        >
          <DownloadSimple size={14} /> CSV
        </button>
      }
    >
      <div className="mb-3 inline-flex rounded-md border border-border bg-muted/40 p-0.5" data-testid="stats-breakdown-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded px-3 py-1 text-xs font-medium transition-colors duration-150 ${tab === t.id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`} data-testid={`stats-breakdown-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="-mx-3 overflow-x-auto sm:mx-0">
        <table className="w-full min-w-[640px] text-xs" data-testid="stats-breakdown-table">
          <thead>
            <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
              {COLS.map((c) => (
                <th key={c.key} className={`cursor-pointer select-none whitespace-nowrap px-3 py-2 font-semibold hover:text-foreground ${c.align === "left" ? "text-left" : "text-right"}`} onClick={() => toggle(c.key)} data-testid={`stats-sort-${c.key}`}>
                  {c.label}
                  {sort.key === c.key && (sort.dir < 0 ? <CaretDown size={10} className="ml-0.5 inline" /> : <CaretUp size={10} className="ml-0.5 inline" />)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/60 transition-colors duration-150 hover:bg-muted/40" data-testid={`stats-row-${r.id}`}>
                <td className="max-w-[220px] truncate px-3 py-2 font-medium text-foreground">{r.name}</td>
                <td className="px-3 py-2 text-right font-mono">{eur(r.revenue)}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="hidden h-1.5 w-16 overflow-hidden rounded-full bg-muted sm:block">
                      <div className="h-full rounded-full bg-[#11A285]" style={{ width: `${(r.occupancy / maxOcc) * 100}%` }} />
                    </div>
                    <span className="font-mono">{num(r.occupancy, 1)} %</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono">{eur(r.adr, 2)}</td>
                <td className="px-3 py-2 text-right font-mono">{eur(r.revpar, 2)}</td>
                <td className="px-3 py-2 text-right font-mono">{num(r.nights)}</td>
                <td className="px-3 py-2 text-right font-mono">{num(r.bookings)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={COLS.length} className="py-8 text-center text-muted-foreground">Nema podataka.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
}
